<template>
    <div class="container-fluid px-4">
        <BaseTable heading="Person Skills" :rows="skills" :columns="columns" searchable :selectable="false"
            :loading="loading" :pagination="pagination" @changePage="onChangePage" @updatePageSize="onUpdatePageSize"
            @searchChanged="onSearchChanged">

            <template #buttons>
                <!-- Admin view: Show manage skills button -->
                <button v-if="!isVolunteerView" class="btn btn-info btn-sm" @click="openAssignSkillModal()">
                    Manage User's skills <i class="material-icons text-lg text-white" color="#e3c9">add</i>
                </button>
                <!-- Volunteer view: Show request to serve button -->
                <button v-if="isVolunteerView" 
                    class="btn btn-success btn-sm" 
                    @click="requestServeInOtherAreas()">
                    Request to serve in other areas <i class="material-icons text-lg text-white">add_circle</i>
                </button>
            </template>

            <template #col-workgroups="{ row }">
                <ul v-if="row.workgroups && row.workgroups.length > 0" class="small mb-0 ps-0" style="list-style-type: none;">
                    <li v-for="(wg, index) in row.workgroups" :key="index" class="mb-1">
                        <span class="me-1">•</span>
                        <span class="fw-medium">{{ wg.activity_name }}</span>
                        <span class="text-muted mx-1">-></span>
                        <span class="fw-semibold">{{ wg.workgroup_name }}</span>
                    </li>
                </ul>
                <span v-else class="text-muted small">No workgroups</span>
            </template>

            <template v-if="!isVolunteerView" #col-skillLevel="{ row }">
                <div class="d-flex align-items-center gap-2">
                    <!-- Material Input -->
                    <MaterialInput :id="`skill-${row.id}`" type="number" v-model="row.skillLevel" placeholder="0–10"
                        min="0" max="10" :style="{ width: '80px' }" />

                    <!-- Save button -->
                    <MaterialButton size="sm" color="success" @click="saveSkill(row)">
                        Save
                    </MaterialButton>
                </div>
                <small class="text-muted">(10 = Expert Level)</small>
            </template>

            <!-- Actions column -->
            <template v-if="!isVolunteerView" #actions="{ row }">
                <MaterialButton size="sm" variant="text" @click="editSkill(row)">
                    <i class="material-icons text-lg text-orange me-1">edit</i>
                </MaterialButton>
            </template>
        </BaseTable>

        <!-- Delete modal -->
        <DeleteModal v-model:show="showDeleteModal" :itemName="selectedSkill?.name" @confirm="deleteSkill" />

        <!-- Edit Modal -->
        <ContentModal v-model="showModal" title="Edit Skill">
            <template #default>
                <div class="p-2 space-y-3">
                    <!-- Disabled Skill Name -->
                    <MaterialInput id="edit-skill-name" label="Skill Name" v-model="editForm.name" disabled />

                    <!-- Disabled Description -->
                    <MaterialInput id="edit-skill-description" label="Description" v-model="editForm.description"
                        disabled />

                    <!-- Editable Skill Level -->
                    <MaterialInput id="edit-skill-level" type="number" label="Skill Level" v-model="editForm.skillLevel"
                        placeholder="0–10" />
                </div>
            </template>
            <template #footer>
                <MaterialButton color="success" @click="updateSkill">
                    Save Changes
                </MaterialButton>
            </template>
        </ContentModal>

        <ContentModal v-model="showAssignPersonSkillModal" title="Assign Person Skill">
            <AssignPersonSkill />
        </ContentModal>
    </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, inject } from "vue";
import { useStore } from "vuex";
import BaseTable from "@/components/Table/Table.vue";
import DeleteModal from "@/components/Modal/ConfirmationModal.vue";
import ContentModal from "@/components/Modal/ContentModal.vue";
import MaterialInput from "@/components/common/MaterialInput.vue";
import MaterialButton from "@/components/common/MaterialButton.vue";
import AssignPersonSkill from "./AssignPersonSkill.vue";
import { getLocationId } from "../../../utils/SessionDataHandler";
import { useRoute } from "vue-router";

const props = defineProps({
    personId: {
        type: Number,
        default: null
    },
    isVolunteerView: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['openServeInOtherAreas']);

const store = useStore();
const route = useRoute();
const loading = ref(true);

// Use prop personId if provided, otherwise inject (modal) or route params
const _injectedPersonId = inject('editUserPersonId', null);
const personId = props.personId || _injectedPersonId?.value || Number(route.params.userid);
const locationId = getLocationId();

// state
const skills = ref([]);
const columns = computed(() => {
    const baseColumns = [
        { key: "name", label: "Skill Name" },
        { key: "description", label: "Description" },
        { key: "workgroups", label: "Skill Workgroups" },
    ];
    
    // Only include skillLevel column if not in volunteer view
    if (!props.isVolunteerView) {
        baseColumns.push({ key: "skillLevel", label: "Skill Level" });
    }
    
    return baseColumns;
});

// Computed property to determine if button should be highlighted
// const shouldHighlightButton = computed(() => {
//     // Only highlight in volunteer view
//     if (!props.isVolunteerView) {
//         return false;
//     }
    
//     // Check if user is NOT an admin in the location
//     const stSessionData = store.getters.stSessionData;
//     const isAdmin = stSessionData?.isAdminInLocation || false;
//     if (isAdmin) {
//         return false;
//     }
    
//     // Check if user has no skills
//     const hasSkills = skills.value.length > 0;
//     if (hasSkills) {
//         return false;
//     }
    
//     // User is not admin AND has no skills - highlight the button
//     return true;
// });

const showDeleteModal = ref(false);
const selectedSkill = ref(null);
const showModal = ref(false);
const showAssignPersonSkillModal = ref(false);

// For editing in modal
const editForm = ref({ id: null, name: "", description: "", skillLevel: 0 });

// Pagination & search
const searchTerm = ref("");
const pagination = computed(() => store.getters.personSkillsPagination || {});
// support older shapes: pageSize or page_size
const resolvedPageSize = () => pagination.value.pageSize ?? pagination.value.page_size ?? 10;

onMounted(async () => {
    loading.value = true;
    try {
        await fetchSkills();
    } finally {
        loading.value = false;
    }
});

// ---- API / store interaction ----
const fetchSkills = async (page = 1, pageSize = resolvedPageSize()) => {
    loading.value = true;
    try {
        // dispatch will commit pagination into store
        const data = await store.dispatch("fetchPersonSkills", {
            personId,
            locationId,
            page,
            pageSize,
            search: searchTerm.value,
        });

        // normalize result to UI shape (handle different API shapes gracefully)
        const returned = data || store.getters.getPersonSkills || [];
        console.log("data", data)
        skills.value = (returned || []).map((s) => {
            // Handle workgroups - could be array, string, or undefined
            let workgroups = [];
            if (s.workgroups) {
                if (Array.isArray(s.workgroups)) {
                    workgroups = s.workgroups;
                } else if (typeof s.workgroups === 'string') {
                    try {
                        workgroups = JSON.parse(s.workgroups);
                    } catch (e) {
                        console.warn('Failed to parse workgroups JSON:', e);
                        workgroups = [];
                    }
                }
            }
            
            return {
                id: s.id,
                name: s.name,
                description: s.description,
                skillLevel: s.skill_level ?? s.skillLevel ?? 0,
                workgroups: workgroups,
            };
        });
    } finally {
        loading.value = false;
    }
};

// Save from table row
const saveSkill = async (row) => {
    loading.value = true;
    try {
        await store.dispatch("saveSkillLevel", {
            skillId: row.id,
            locationId,
            skillLevel: row.skillLevel,
            personId,
        });
        // optionally refresh current page if you want authoritative data from backend:
        // await fetchSkills(pagination.value.currentPage ?? 1, resolvedPageSize());
        await fetchSkills();
    } catch (e) {
        // swallow or show toast depending on app conventions
        console.error("Failed to save skill level", e);
    }
    finally {
        loading.value = false;
    }
};

// Edit modal
const editSkill = (row) => {
    editForm.value = { ...row };
    showModal.value = true;
};

const openAssignSkillModal = () => {
    showAssignPersonSkillModal.value = true;
};

const requestServeInOtherAreas = () => {
    emit('openServeInOtherAreas');
};

const updateSkill = async () => {
    loading.value = true;
    try {
        await store.dispatch("saveSkillLevel", {
            skillId: editForm.value.id,
            locationId,
            skillLevel: editForm.value.skillLevel,
            personId,
        });
        // Refresh table from API to get updated data
        await fetchSkills(pagination.value.currentPage ?? pagination.value.current_page ?? 1, resolvedPageSize());
        showModal.value = false;
    } catch (e) {
        console.error("Failed to update skill level", e);
    } finally {
        loading.value = false;
    }
};

// Delete handler
const deleteSkill = () => {
    if (!selectedSkill.value) return;
    skills.value = skills.value.filter((s) => s.id !== selectedSkill.value.id);
    showDeleteModal.value = false;
};

// ---- Pagination & Search handlers (wired to BaseTable) ----
const onChangePage = async (page) => {
    await fetchSkills(page, resolvedPageSize());
};

const onUpdatePageSize = async (size) => {
    // reset to first page when page size changes
    await fetchSkills(1, size);
};

const onSearchChanged = async (term) => {
    searchTerm.value = term;
    await fetchSkills(1, resolvedPageSize());
};

// Watch for AssignPersonSkill modal close to refresh table data
// This ensures the table is updated after saving from the AssignPersonSkill modal
watch(showAssignPersonSkillModal, async (newVal, oldVal) => {
    // When modal closes (changes from true to false), refresh the table from API
    if (oldVal === true && newVal === false) {
        await fetchSkills(pagination.value.currentPage ?? pagination.value.current_page ?? 1, resolvedPageSize());
    }
});
</script>